import { cookiesEnabled, readCookie } from '../core/cookies.js';

/**
 * Diagnostic events (spec §3) — the ones that measure whether the measurement works.
 *
 * These are built first on purpose: every later phase relies on them to notice a
 * broken rollout in hours instead of the eight months the checkout events were dead.
 */

const MAX_JS_ERRORS = 10;

const MAX_API_FAILURES = 10;

const TRACKED_ENDPOINT = /(admin-ajax\.php|wc-ajax=|\/wp-json\/)/;

export function collectDiagnostics(tracker, { win = window, doc = document } = {}) {
  collectJsErrors(tracker, win);
  collectFailedRequests(tracker, win);
  collectCookieBlocked(tracker, win, doc);
}

/**
 * js_error — uncaught errors and rejected promises.
 *
 * Capped per page load: a broken script in a render loop would otherwise send
 * thousands of events and hide everything else.
 */
export function collectJsErrors(tracker, win = window) {
  let sent = 0;

  const report = (params) => {
    if (sent >= MAX_JS_ERRORS) {
      return;
    }
    sent++;

    tracker.track('js_error', Object.assign({ browser_context: browserContext(win) }, params));
  };

  win.addEventListener('error', (event) => {
    // Resource load failures surface as error events on the element, not on window.
    if (event && event.target && event.target !== win && event.target.tagName) {
      const tag = event.target.tagName.toLowerCase();

      if (tag === 'script' || tag === 'img' || tag === 'link') {
        report({
          message: 'resource failed to load',
          source_file: String(event.target.src || event.target.href || ''),
          line: 0,
          error_type: 'resource',
        });
      }

      return;
    }

    report({
      message: String((event && event.message) || 'unknown error').slice(0, 500),
      source_file: String((event && event.filename) || ''),
      line: Number((event && event.lineno) || 0),
      column: Number((event && event.colno) || 0),
      error_type: 'uncaught',
    });
  }, true);

  win.addEventListener('unhandledrejection', (event) => {
    const reason = event && event.reason;

    report({
      message: String((reason && reason.message) || reason || 'unhandled rejection').slice(0, 500),
      source_file: '',
      line: 0,
      error_type: 'unhandled_rejection',
    });
  });
}

/**
 * api_request_failed — HTTP >= 400 or a network failure on our own endpoints.
 *
 * Tells "the customer gave up" apart from "the system failed", which is the difference
 * between a UX problem and an outage.
 */
export function collectFailedRequests(tracker, win = window) {
  const collectUrl = String(tracker.context.collect_url || '');
  let sent = 0;

  const report = (endpoint, status, code) => {
    const url = String(endpoint);

    if (!TRACKED_ENDPOINT.test(url)) {
      return;
    }

    // Our own collect endpoint can sit behind admin-ajax; reporting its failures over
    // itself is a loop.
    if (collectUrl && url.indexOf(collectUrl) !== -1) {
      return;
    }

    if (sent >= MAX_API_FAILURES) {
      return;
    }
    sent++;

    tracker.track('api_request_failed', {
      endpoint: shorten(endpoint),
      http_status: Number(status) || 0,
      error_code: String(code || ''),
      page_path: tracker.context.page_path,
    });
  };

  // Wrapping an already wrapped transport would report every failure twice.
  if (win.__trackdaddyTransportPatched) {
    return;
  }
  win.__trackdaddyTransportPatched = true;

  const originalFetch = win.fetch;

  if (typeof originalFetch === 'function') {
    win.fetch = function trackdaddyFetch(input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';

      return originalFetch.apply(this, arguments).then(
        (response) => {
          if (response && !response.ok) {
            report(url, response.status, 'http_error');
          }

          return response;
        },
        (error) => {
          report(url, 0, (error && error.name) || 'network_error');

          throw error;
        }
      );
    };
  }

  const XHR = win.XMLHttpRequest;

  if (typeof XHR === 'function' && XHR.prototype) {
    const open = XHR.prototype.open;
    const send = XHR.prototype.send;

    XHR.prototype.open = function trackdaddyOpen(method, url) {
      this.__trackdaddyUrl = url;

      return open.apply(this, arguments);
    };

    XHR.prototype.send = function trackdaddySend() {
      this.addEventListener('loadend', () => {
        if (this.status === 0) {
          report(this.__trackdaddyUrl, 0, 'network_error');
        } else if (this.status >= 400) {
          report(this.__trackdaddyUrl, this.status, 'http_error');
        }
      });

      return send.apply(this, arguments);
    };
  }

  // jQuery's transport is XMLHttpRequest, so the loadend hook above already covers the
  // WooCommerce AJAX paths — an ajaxError listener would only duplicate them.
}

/**
 * cookie_blocked — separates lost data from absent traffic.
 */
export function collectCookieBlocked(tracker, win = window, doc = document) {
  const enabled = cookiesEnabled(doc);
  const visitorCookie = readCookie('_td_sid', doc);
  const storage = storageAvailable(win);

  if (enabled && visitorCookie && storage) {
    return;
  }

  let blockedType = 'unknown';

  if (!enabled) {
    blockedType = 'cookies';
  } else if (!storage) {
    blockedType = 'storage';
  } else if (!visitorCookie) {
    blockedType = 'visitor_cookie';
  }

  tracker.track(
    'cookie_blocked',
    {
      blocked_type: blockedType,
      browser: browserContext(win),
    },
    { once: 'session', dedupeKey: 'cookie_blocked:' + blockedType }
  );
}

function storageAvailable(win) {
  try {
    win.localStorage.setItem('_td_probe', '1');
    win.localStorage.removeItem('_td_probe');

    return true;
  } catch (e) {
    return false;
  }
}

/**
 * In-app WebViews are the reason add_to_cart reads 2.67% in Facebook against 9.91% in
 * Chrome, so every diagnostic says which shell it came from.
 */
export function browserContext(win = window) {
  const ua = (win.navigator && win.navigator.userAgent) || '';

  if (/FBAN|FBAV|FB_IAB/i.test(ua)) {
    return 'facebook_webview';
  }

  if (/Instagram/i.test(ua)) {
    return 'instagram_webview';
  }

  if (/TikTok|BytedanceWebview/i.test(ua)) {
    return 'tiktok_webview';
  }

  if (/\bwv\b|; wv\)/i.test(ua)) {
    return 'android_webview';
  }

  return 'browser';
}

function shorten(url) {
  const value = String(url || '');

  try {
    const parsed = new URL(value, 'https://placeholder.invalid');
    const action = parsed.searchParams.get('action') || parsed.searchParams.get('wc-ajax');

    return parsed.pathname + (action ? '?action=' + action : '');
  } catch (e) {
    return value.split('?')[0].slice(0, 200);
  }
}
